/**
 * ConferenciaItemsTable — Table of items in a conferência.
 * Shows product, description, unit, lot, expiry, expected, counted, difference.
 */
import React, { useMemo, useEffect, useRef } from 'react';
import { ItemConferenciaVerif } from '../../services/conferenciaService';
import { EditableNumberCell } from '../contagem/EditableNumberCell';

interface Props {
  itens: ItemConferenciaVerif[];
  loading: boolean;
  lastScannedItemId: number | null;
  isEditMode?: boolean;
  modifiedItems?: Map<number, number>;
  onUpdateItemField?: (itemId: number, field: string, value: number) => void;
}

export const ConferenciaItemsTable: React.FC<Props> = ({
  itens, loading, lastScannedItemId,
  isEditMode = false, modifiedItems, onUpdateItemField,
}) => {
  const tableRef = useRef<HTMLDivElement>(null);

  // Scroll to last scanned item
  useEffect(() => {
    if (lastScannedItemId && tableRef.current) {
      const row = tableRef.current.querySelector(`[data-item-id="${lastScannedItemId}"]`);
      if (row) {
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [lastScannedItemId]);

  // Group by lacre if any
  const sortedItens = useMemo(() => {
    return [...itens].sort((a, b) => {
      // Last scanned item first
      if (a.id === lastScannedItemId) return -1;
      if (b.id === lastScannedItemId) return 1;
      // Then by lacre, then codigo_produto
      const lacreCompare = (a.lacre_selecionado || '').localeCompare(b.lacre_selecionado || '');
      if (lacreCompare !== 0) return lacreCompare;
      return a.codigo_produto.localeCompare(b.codigo_produto);
    });
  }, [itens, lastScannedItemId]);

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3" />
        <p className="text-sm text-gray-500">Carregando itens...</p>
      </div>
    );
  }

  if (itens.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
        <p className="text-gray-500 dark:text-gray-400">
          Nenhum item. Selecione lacres para carregar os itens da contagem.
        </p>
      </div>
    );
  }

  return (
    <div ref={tableRef} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Itens ({itens.length})
        </h4>
        <div className="text-xs text-gray-500">
          Conferidos: {itens.filter(i => i.quantidade_conferida > 0).length} de {itens.length}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/50">
            <tr>
              <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Produto</th>
              <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Descrição</th>
              <th className="text-center px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Un</th>
              <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Lote</th>
              <th className="text-center px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Validade</th>
              {itens.some(i => i.lacre_selecionado) && (
                <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Lacre</th>
              )}
              <th className="text-right px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Coletado</th>
              <th className="text-right px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Contado</th>
              <th className="text-right px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Difer.</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {sortedItens.map((item) => {
              const isLastScanned = item.id === lastScannedItemId;
              const difColor = item.diferenca > 0
                ? 'text-green-600 dark:text-green-400'
                : item.diferenca < 0
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-gray-500';

              return (
                <tr
                  key={item.id}
                  data-item-id={item.id}
                  className={`transition-colors ${
                    isLastScanned
                      ? 'bg-yellow-50 dark:bg-yellow-900/20 animate-pulse'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
                  }`}
                >
                  <td className="px-3 py-2 font-mono text-xs">{item.codigo_produto}</td>
                  <td className="px-3 py-2 max-w-[250px] truncate" title={item.descricao}>
                    {item.descricao}
                  </td>
                  <td className="px-3 py-2 text-center text-xs">{item.unidade}</td>
                  <td className="px-3 py-2 font-mono text-xs">{item.lote || '—'}</td>
                  <td className="px-3 py-2 text-center text-xs">{item.data_validade || '—'}</td>
                  {itens.some(i => i.lacre_selecionado) && (
                    <td className="px-3 py-2 text-xs">{item.lacre_selecionado || '—'}</td>
                  )}
                  <td className="px-3 py-2 text-right font-mono">{item.quantidade_esperada}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold">
                    {isEditMode && onUpdateItemField ? (
                      <EditableNumberCell
                        value={item.quantidade_conferida}
                        rowIndex={item.id}
                        fieldName="quantidade_conferida"
                        isReadOnly={false}
                        isModified={modifiedItems?.has(item.id) || false}
                        onUpdate={onUpdateItemField}
                      />
                    ) : (
                      item.quantidade_conferida
                    )}
                  </td>
                  <td className={`px-3 py-2 text-right font-mono font-bold ${difColor}`}>
                    {item.diferenca > 0 ? '+' : ''}{item.diferenca}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
